/* Core & libraries */
import React, { useState, useEffect } from "react";
import { useTheme } from "react-jss";
import { FormattedMessage } from "react-intl";
import { Accordion } from "react-bootstrap";
import { Link } from "react-router-dom";
import { ReactSVG } from "react-svg";

/* Helpers, constants, custom methods, internal libraries */
import { useHasPermission } from "../../../hooks/useHasPermission";
import { useDiscountService } from "services/discountService";

/* Components */
import UCButton from "../../../components/buttons";
import { ReactComponent as LockIcon } from "../../../resources/images/aa-discounts-lock.svg";
import { ReactComponent as ArrowRightIcon } from "../../../resources/images/arrow-right.svg";
import { ReactComponent as PhoneIcon } from "../../../resources/images/aa-discounts-phone.svg";
import { ReactComponent as TermsLockIcon } from "../../../resources/images/aa-discounts-lock-unlocked.svg";
import { ReactComponent as ChevronUpSvg } from "../../../resources/images/chevron_big_up.svg";
import { ReactComponent as ChevronDownSvg } from "../../../resources/images/chevron_big_down.svg";

/* Styles */
import styles from "./styles";

const BenefitsCard = ({ data, onSuccess, beResponse }) => {
  const theme = useTheme();
  const classes = styles({ theme });

  const { checkPermission } = useHasPermission();
  const { postDiscountTerms } = useDiscountService();

  const [openAccordions, setOpenAccordions] = useState({});

  useEffect(() => {
    if (!data) return;

    if (data?.currentTier === false) {
      setOpenAccordions({ 0: true, 1: true });
    } else if (
      typeof data?.currentTierViewBenefitsId === "number" &&
      data?.currentTierViewBenefitsId >= 0
    ) {
      setOpenAccordions({ [data?.currentTierViewBenefitsId]: true });
    } else {
      setOpenAccordions({ 0: true });
    }
  }, [data]);

  const lastUnlockableItem = data?.contentData
    ?.slice()
    .reverse()
    .find((item) => item.allowUnlock);

  const handleOptIn = async () => {
    const success = await postDiscountTerms({
      action: "ACCEPT",
      tierCode: lastUnlockableItem?.tierCode || "",
      discountCode: lastUnlockableItem?.discountCode || "",
    });
    if (success) {
      onSuccess();
    }
  };

  const toggleAccordion = (index) => {
    setOpenAccordions((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  return (
    <>
      {beResponse ? (
        <div className={classes.benefitsWrapper}>
          {checkPermission("F_DISCOUNTTIER_MANAGETIERLEVEL") &&
            lastUnlockableItem && (
              <UCButton
                className={classes.optInBtn}
                type="primary"
                onClick={handleOptIn}
                text={
                  <>
                    <TermsLockIcon />
                    <FormattedMessage id="discounts.optInButton" />
                  </>
                }
                spinner={{
                  name: "discountTermsDataSpinner",
                  size: 20,
                }}
              />
            )}
          {data?.contentData?.map((dataItem, i) => {
            return (
              <div className={classes.benefitsCard} key={i}>
                <div
                  className={`${classes.benefitsCardHeader} ${i === 0 && lastUnlockableItem && classes.optInBtnPadding}`}
                >
                  <div>
                    <h6>{dataItem.title}</h6>
                    <p>{dataItem.description}</p>
                  </div>
                  <UCButton
                    type="defaultLink"
                    onClick={() => toggleAccordion(i)}
                    className={classes.accordionBtn}
                    text={
                      openAccordions[i] ? (
                        <FormattedMessage id="discounts.hideBenefitsButton" />
                      ) : (
                        <FormattedMessage id="discounts.viewBenefitsButton" />
                      )
                    }
                    iconEnd={
                      openAccordions[i] ? <ChevronUpSvg /> : <ChevronDownSvg />
                    }
                  />
                </div>
                <Accordion activeKey={openAccordions[i] ? `${i}` : undefined}>
                  <Accordion.Collapse eventKey={`${i}`}>
                    <div className={classes.cardsWrapper}>
                      {dataItem?.benefits?.map((item) => {
                        return (
                          <div className={classes.cardBox} key={item.title}>
                            <div
                              className={
                                dataItem.activeIcons
                                  ? classes.activeIcon
                                  : classes.lockedIcon
                              }
                            >
                              {dataItem.activeIcons ? (
                                <ReactSVG src={item.logo} />
                              ) : (
                                <LockIcon />
                              )}
                            </div>
                            <h6>{item.title}</h6>
                            <p>{item.description}</p>
                            {item.linkUrl && (
                              <Link
                                to={item.linkUrl}
                                target="_blank"
                                className={classes.link}
                              >
                                {item.linkText || (
                                  <FormattedMessage id="discounts.learnMoreButton" />
                                )}
                                <ArrowRightIcon />
                              </Link>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </Accordion.Collapse>
                </Accordion>
              </div>
            );
          })}
        </div>
      ) : (
        <div className={classes.benefitsWrapper}>
          <div className={classes.tierUnavailableWrapper}>
            <h6>
              <FormattedMessage id="discounts.benefitsUnavailableTitle" />
            </h6>
            <p>
              <FormattedMessage id="discounts.benefitsUnavailableDescription" />
            </p>
          </div>
        </div>
      )}
      {data?.currentTier && (
        <>
          <hr className={classes.divider} />
          <div className={classes.footerItemWrapper}>
            <div>
              <PhoneIcon />
            </div>
            <div className={classes.footerItem}>
              <h6>
                <FormattedMessage id="discounts.agencyTitle" />
              </h6>
              <p>
                <FormattedMessage id="discounts.agencyDescription" />
              </p>
              <Link to={data?.pdfPath} target="_blank" className={classes.link}>
                <FormattedMessage id="discounts.agencyButton" />
                <ArrowRightIcon />
              </Link>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default BenefitsCard;
